import { useEffect, useMemo, useState } from "react";
import { Form, Col, Row, Button, Input } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import {
  CheckCircleOutlined,
  CloseOutlined,
  UnorderedListOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import useDelete from "../../../global/hooks/useDelete";
import ConfirmModal from "../../../global/components/ConfirmModal";
import { RiAlignItemBottomFill } from "react-icons/ri";
import { getModifiedResponse } from "../../../global/utils/responseModifier";

const uuid4 = () => {
  const cryptoApi =
    typeof window !== "undefined" ? window.crypto || window.msCrypto : null;
  if (!cryptoApi || typeof cryptoApi.getRandomValues !== "function") {
    return `plate-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  return [...bytes]
    .map((byte, i) =>
      [4, 6, 8, 10].includes(i)
        ? `-${byte.toString(16).padStart(2, "0")}`
        : byte.toString(16).padStart(2, "0"),
    )
    .join("");
};

export const PlatesForm = ({ type }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedOemNo, setSelectedOemNo] = useState(null);
  const [selectedDealerNo, setSelectedDealerNo] = useState(null);
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const url = id && `${process.env.REACT_APP_API_URL}/api/plates/${id}/`;
  const postUrl = `${process.env.REACT_APP_API_URL}/api/plates/`;
  const putUrl = `${process.env.REACT_APP_API_URL}/api/plates/${id}/`;
  const deleteUrl = `${process.env.REACT_APP_API_URL}/api/plates/${id}/`;
  const accountOptionUrl = `${process.env.REACT_APP_API_URL}/api/accounts/options/`;

  const { data, loading, refetch } = useGet(url);
  const { data: accountOptionsData } = useGet(accountOptionUrl);

  const dealerOptionsUrl = selectedOemNo
    ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${selectedOemNo}/dealers/`
    : null;
  const { data: dealerOptionsData } = useGet(dealerOptionsUrl);

  const effectiveDealerNo = selectedDealerNo || null;
  const deviceOptionsUrl = effectiveDealerNo
    ? `${process.env.REACT_APP_API_URL}/api/devices/?DealerNo=${effectiveDealerNo}&limit=1000`
    : null;
  const { data: deviceOptionsData } = useGet(deviceOptionsUrl);

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);
  const {
    data: putData,
    loading: putLoading,
    error: putError,
    putData: putAction,
  } = usePut(putUrl);
  const {
    data: deletedData,
    error: deletedDataError,
    deleteData,
  } = useDelete(deleteUrl);

  const modifiedOems = useMemo(
    () => getModifiedResponse(accountOptionsData, "oems"),
    [accountOptionsData],
  );
  const modifiedDealers = useMemo(
    () => getModifiedResponse(dealerOptionsData, null),
    [dealerOptionsData],
  );
  const modifiedDevices = useMemo(() => {
    const devices = deviceOptionsData?.data || [];
    if (!Array.isArray(devices)) return [];
    return devices.map((item) => ({
      label: item?.DeviceName
        ? `${item.DeviceName} (${item.DeviceNo})`
        : `${item?.DeviceNo || ""}`,
      value: item?.DeviceNo,
    }));
  }, [deviceOptionsData]);

  const generateAccessCode = () => uuid4();

  const onFinish = async (values) => {
    const payload = {
      ...values,
      AccessCode:
        values?.AccessCode && String(values.AccessCode).trim() !== ""
          ? values.AccessCode
          : id
            ? data?.data?.AccessCode || generateAccessCode()
            : generateAccessCode(),
    };
    delete payload.OemNo;

    try {
      if (id) {
        await putAction(payload);
      } else {
        await postAction(payload);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
  };

  const onReset = () => {
    navigate(-1);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteData();
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };

  useEffect(() => {
    if (!id || loading) return;
    try {
      form.resetFields();
      if (data?.data) {
        form.setFieldsValue({
          PlateName: data?.data?.PlateName ? String(data.data.PlateName) : "",
          PlateDesc: data?.data?.PlateDesc ? String(data.data.PlateDesc) : "",
          AccessCode: data?.data?.AccessCode
            ? String(data.data.AccessCode)
            : "",
          OemNo:
            data?.data?.OemNo || data?.data?.OemNo === 0
              ? Number(data.data.OemNo)
              : undefined,
          DealerNo:
            data?.data?.DealerNo || data?.data?.DealerNo === 0
              ? Number(data.data.DealerNo)
              : undefined,
          DeviceNo:
            data?.data?.DeviceNo || data?.data?.DeviceNo === 0
              ? Number(data.data.DeviceNo)
              : undefined,
          DoorNo:
            data?.data?.DoorNo || data?.data?.DoorNo === 0
              ? String(data.data.DoorNo)
              : "",
        });
        setSelectedOemNo(data?.data?.OemNo || null);
        setSelectedDealerNo(data?.data?.DealerNo || null);
      }
    } catch (error) {
      if (!loading) {
        openErrorNotification("Error fetching data!");
      }
    }
  }, [id, data, loading, form, type]);

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      openConfirmNotification("Success");
      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [postError, postData, postLoading]);

  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong",
      );
    } else if (!putLoading && !putError && putData) {
      openConfirmNotification("Success");
      refetch();
      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [putData, putLoading, putError]);

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      navigate("/plate");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  const breadcrumbItems = [
    {
      label: "Plate",
      href: "/plate",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/plate/info/${id}`
          : `/plate/edit/${id}`
        : "/plate/add",
    },
  ];

  const buttonsData =
    type === "info"
      ? [
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => {
              navigate(`/plate/edit/${id}`);
            },
          },
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => {
              setShowModal(true);
            },
          },
          {
            label: "List",
            icon: <UnorderedListOutlined />,
            onClick: () => {
              navigate(-1);
            },
          },
        ]
      : [
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ];

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div>
          <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
            <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
              <CommonButton buttonsData={buttonsData} />
            </CommonBreadcrumb>
          </div>
          <div className="bg-white overflow-hidden border rounded-[4px]">
            <div className="flex items-center bg-gray-200 p-2.5">
              <RiAlignItemBottomFill className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2 ">
                Plate
              </span>
            </div>
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomInput
                    label="Plate Name"
                    name="PlateName"
                    disabled={type === "info"}
                    required
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomInput
                    label="Description"
                    name="PlateDesc"
                    disabled={type === "info"}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <Form.Item
                    label={
                      <span>
                        Access Code
                        <span style={{ color: "red" }}>*</span>
                      </span>
                    }
                    rules={[
                      { required: true, message: "Access Code is required" },
                    ]}
                    required={false}
                  >
                    <div className="flex gap-2 items-center">
                      <Form.Item name="AccessCode" noStyle className="flex-1 min-w-0">
                        <Input
                          disabled={type === "info"}
                          className={`rounded-[4px] ${
                            type === "info" ? "!bg-[#fafbfc]" : ""
                          }`}
                          style={{
                            color: type === "info" ? "#000000" : "initial",
                            height: "32px",
                          }}
                        />
                      </Form.Item>
                      <Button
                        type="primary"
                        htmlType="button"
                        icon={<ReloadOutlined />}
                        className="h-[32px] px-2 shrink-0"
                        disabled={type === "info"}
                        onClick={() => {
                          try {
                            form.setFieldsValue({
                              AccessCode: generateAccessCode(),
                            });
                          } catch (error) {
                            form.setFieldsValue({
                              AccessCode: `plate-${Date.now()}`,
                            });
                          }
                        }}
                      />
                    </div>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomSelect
                    label="OEM"
                    name="OemNo"
                    options={modifiedOems}
                    disabled={type === "info"}
                    required
                    placeholder="Please select"
                    onChange={(value) => {
                      setSelectedOemNo(value || null);
                      setSelectedDealerNo(null);
                      form.setFieldsValue({
                        OemNo: value || undefined,
                        DealerNo: undefined,
                        DeviceNo: undefined,
                      });
                    }}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomSelect
                    label="Dealer"
                    name="DealerNo"
                    options={modifiedDealers}
                    disabled={
                      type === "info" ||
                      !(selectedOemNo || form.getFieldValue("OemNo"))
                    }
                    required
                    placeholder="Please select"
                    onChange={(value) => {
                      setSelectedDealerNo(value || null);
                      form.setFieldsValue({
                        DealerNo: value || undefined,
                        DeviceNo: undefined,
                      });
                    }}
                    formItemProps={{
                      rules: [
                        { required: true, message: "Dealer is required" },
                      ],
                    }}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomSelect
                    label="Site"
                    name="DeviceNo"
                    options={modifiedDevices}
                    disabled={
                      type === "info" ||
                      !(selectedDealerNo || form.getFieldValue("DealerNo"))
                    }
                    required
                    placeholder="Please select"
                    formItemProps={{
                      rules: [{ required: true, message: "Site is required" }],
                    }}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} span={24}>
                  <CustomInput
                    label="Door No"
                    name="DoorNo"
                    disabled={type === "info"}
                    required
                    loading={loading}
                    type="number"
                    min={0}
                  />
                </Col>
              </Row>
            </div>
          </div>
        </div>
      </Form>

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
    </>
  );
};
